import { Widget, codicon } from '@theia/core/lib/browser/widgets/widget';
import { MessageLoop } from '@theia/core/shared/@lumino/messaging';

export type OverlayPanelOptions = {
    title: string;
    /** Panel max-width. Default: '1100px'. */
    width?: string;
    /** Panel max-height. Default: '700px'. */
    height?: string;
};

/**
 * A reusable floating overlay panel that hosts any Lumino {@link Widget}.
 *
 * Uses fully self-contained CSS classes (lvgl-overlay-*) so there is no
 * dependency on Theia's .lm-Widget scoped dialog CSS.
 */
export class OverlayPanel {
    readonly node: HTMLDivElement;

    protected readonly panelNode: HTMLDivElement;
    protected readonly titleNode: HTMLSpanElement;
    protected readonly closeButtonNode: HTMLElement;
    protected readonly bodyNode: HTMLDivElement;

    private contentWidget: Widget | undefined;
    private onKeyDown: ((e: KeyboardEvent) => void) | undefined;

    constructor(protected readonly options: OverlayPanelOptions) {
        // ── Root overlay (backdrop + centering) ───────────────────────────
        this.node = document.createElement('div');
        this.node.className = 'lvgl-overlay-root';
        this.node.style.display = 'none';
        this.node.addEventListener('mousedown', (e: MouseEvent) => {
            if (e.target === this.node) this.close();
        });

        // ── Floating panel ────────────────────────────────────────────────
        this.panelNode = document.createElement('div');
        this.panelNode.className = 'lvgl-overlay-panel';
        this.panelNode.style.maxWidth = options.width ?? '1100px';
        this.panelNode.style.maxHeight = options.height ?? '700px';

        // ── Header ────────────────────────────────────────────────────────
        const header = document.createElement('div');
        header.className = 'lvgl-overlay-header';

        this.titleNode = document.createElement('span');
        this.titleNode.className = 'lvgl-overlay-title';
        this.titleNode.textContent = options.title;

        this.closeButtonNode = document.createElement('i');
        this.closeButtonNode.className = codicon('close', true);
        this.closeButtonNode.classList.add('lvgl-overlay-close');
        this.closeButtonNode.setAttribute('role', 'button');
        this.closeButtonNode.setAttribute('aria-label', 'Close');
        this.closeButtonNode.addEventListener('click', () => this.close());

        header.appendChild(this.titleNode);
        header.appendChild(this.closeButtonNode);

        // ── Body ──────────────────────────────────────────────────────────
        this.bodyNode = document.createElement('div');
        this.bodyNode.className = 'lvgl-overlay-body';

        // Assemble
        this.panelNode.appendChild(header);
        this.panelNode.appendChild(this.bodyNode);
        this.node.appendChild(this.panelNode);
    }

    setContent(widget: Widget): void {
        if (this.contentWidget && this.contentWidget !== widget) {
            Widget.detach(this.contentWidget);
        }
        this.contentWidget = widget;
    }

    open(): void {
        if (!this.node.isConnected) {
            document.body.appendChild(this.node);
            this.onKeyDown = (e: KeyboardEvent) => {
                if (e.key === 'Escape') this.close();
            };
            document.addEventListener('keydown', this.onKeyDown);
        }

        this.node.style.display = 'flex';

        if (this.contentWidget) {
            if (!this.contentWidget.isAttached) {
                Widget.attach(this.contentWidget, this.bodyNode);
            }
            requestAnimationFrame(() => {
                if (this.contentWidget) {
                    const w = this.bodyNode.offsetWidth;
                    const h = this.bodyNode.offsetHeight;
                    MessageLoop.sendMessage(
                        this.contentWidget,
                        w > 0 ? new Widget.ResizeMessage(w, h) : Widget.ResizeMessage.UnknownSize
                    );
                }
            });
        }
    }

    close(): void {
        this.node.style.display = 'none';
    }

    dispose(): void {
        if (this.onKeyDown) {
            document.removeEventListener('keydown', this.onKeyDown);
        }
        if (this.contentWidget?.isAttached) {
            Widget.detach(this.contentWidget);
        }
        this.node.remove();
    }
}
